/**
 * @file order-list.ts
 *
 * Pure helpers for the Orders list/drawer: the customer display name, the
 * line-item count, date/time formatting, address formatting, and a
 * positive-amount predicate. Kept separate from the components so the formatting
 * logic stays independently testable without mounting React. (The page size
 * lives with the API as {@link ORDERS_PER_PAGE}; the row menu action set lives in
 * `order-menu.ts` because it references localized messages.)
 */

import type { ShopOrder, ShopOrderAddress } from '../api/shop-types';

/**
 * Builds the customer's display name from an order's billing details, falling back
 * to the billing email then the order number when no name is present. List rows come
 * from the lighter `/v1` summary (which has only the email, no name), so the list
 * "Customer" column degrades to the email; the drawer fetches the full order, which
 * carries the name.
 *
 * @param order - The order.
 *
 * @returns The customer name, the email, or `#<number>` when unavailable.
 */
export function customerName(order: ShopOrder): string {
  const name = `${order.billing.first_name} ${order.billing.last_name}`.trim();
  return name || order.billing.email || (order.number ? `#${order.number}` : '—');
}

/**
 * Sums the quantities across an order's line items.
 *
 * @param order - The order.
 *
 * @returns The total number of purchased items.
 */
export function orderItemCount(order: ShopOrder): number {
  return order.line_items.reduce((sum, item) => sum + item.quantity, 0);
}

/**
 * Formats an ISO timestamp as a short local date (e.g. `06/15/2026`). Returns a
 * dash for a missing or unparseable value.
 *
 * @param iso - The ISO date string.
 *
 * @returns The formatted date, or `—`.
 */
export function formatOrderDate(iso: string): string {
  if (!iso) {
    return '—';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return date.toLocaleDateString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * Formats an ISO timestamp as a 24-hour local time (e.g. `07:08:42`). Returns a
 * dash for a missing or unparseable value.
 *
 * @param iso - The ISO date string.
 *
 * @returns The formatted time, or `—`.
 */
export function formatOrderTime(iso: string): string {
  if (!iso) {
    return '—';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

/**
 * Joins the non-empty address lines (street lines, `city, state`, and
 * `postcode country`) into newline-separated lines, mirroring the WSB-v2 drawer.
 * The recipient name is rendered separately by the caller, so it is not included
 * here.
 *
 * @param address - The billing or shipping address.
 *
 * @returns The formatted address lines joined by newlines (empty when blank).
 */
export function formatAddress(address: ShopOrderAddress): string {
  return [
    address.address_1,
    address.address_2,
    [address.city, address.state].filter(Boolean).join(', '),
    [address.postcode, address.country].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join('\n');
}

/**
 * Whether a string-encoded money amount is greater than zero. Used to decide
 * which conditional total rows (discount, shipping, tax) the drawer shows.
 *
 * @param value - The amount as a decimal string (e.g. `"10.00"`).
 *
 * @returns `true` when the parsed amount is greater than zero.
 */
export function hasAmount(value: string): boolean {
  return Number(value) > 0;
}

/** The lifecycle statuses an admin can cancel from; the rest are terminal or computed. */
const CANCELLABLE_STATUSES = ['pending', 'on-hold', 'processing'];

/**
 * Whether the Cancel action applies: the order is in a cancellable lifecycle state
 * (`/v1` has no generic status-set endpoint, so cancel is the only lifecycle action).
 *
 * @param order - The order.
 *
 * @returns `true` when the order can be cancelled.
 */
export function canCancelOrder(order: ShopOrder): boolean {
  return CANCELLABLE_STATUSES.includes(order.status);
}

/**
 * Whether the Refund action applies: the order is fully paid. Only full refunds are
 * supported, so partially paid / partially refunded orders are excluded.
 *
 * @param order - The order.
 *
 * @returns `true` when the order can be fully refunded.
 */
export function canRefundOrder(order: ShopOrder): boolean {
  return order.payment_status === 'paid';
}

/**
 * Whether the Mark-as-paid action applies: a `manual`-payment order that is still unpaid.
 * Automated (`shore`) orders settle via gateway callbacks, so they never expose it. Only
 * resolvable on the full order (the list summary carries no payment type), so this is a
 * drawer-only gate.
 *
 * @param order - The order.
 *
 * @returns `true` when a manual unpaid order can be marked paid.
 */
export function canMarkOrderPaid(order: ShopOrder): boolean {
  return order.payment_type === 'manual' && order.payment_status === 'unpaid';
}

/**
 * Whether the Fulfil action applies: a `manual`-payment order that is still unfulfilled
 * and has at least one shippable line. Fulfil ships every shippable line in one shipment,
 * so it is offered only from `unfulfilled` (a partially fulfilled order would over-ship);
 * an all-digital order has nothing to ship (and would send an empty shipment). Automated
 * orders are gateway-driven and do not expose it. Drawer-only (the summary carries no
 * payment type, fulfilment status, or line items).
 *
 * @param order - The order.
 *
 * @returns `true` when a manual unfulfilled order has shippable lines to fulfil.
 */
export function canFulfilOrder(order: ShopOrder): boolean {
  return (
    order.payment_type === 'manual' &&
    order.fulfilment_status === 'unfulfilled' &&
    order.line_items.some((line) => line.shippable)
  );
}
